<script setup lang="ts">
const { isOpen, image360Url } = defineProps<{
  isOpen: boolean;
  image360Url: string;
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { isTouchDevice } = useDeviceInformation();

const isZoomMode = ref(false);

watch(
  () => isOpen,
  () => {
    isZoomMode.value = false;
  },
);

const handleClose = () => {
  emit("close");
};

const toggleViewMode = () => {
  isZoomMode.value = !isZoomMode.value;
};

const currentModeIcon = computed(() => {
  return isZoomMode.value ? "rotate" : "zoomIn";
});

const shouldEnableZoom = computed(() => {
  if (isTouchDevice.value) {
    return true;
  }

  return isZoomMode.value;
});
</script>

<template>
  <BaseModal
    v-if="isOpen"
    size="full"
    :no-padding="true"
    data-test="360-viewer-modal"
    @close="handleClose"
  >
    <div class="relative w-full h-full flex flex-col">
      <div class="flex-1 flex items-center justify-center min-h-0">
        <Base360Viewer
          :image-url="image360Url"
          :enable-zoom="shouldEnableZoom"
          data-test="360-viewer-modal-content"
          class="w-full h-full"
        />
      </div>

      <!-- TODO: Add i18n -->
      <button
        v-if="!isTouchDevice"
        class="z-50 absolute bottom-6 right-6 flex items-center gap-2 px-4 py-2 bg-platform-bg-1 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 border border-platform-border-1 group"
        data-test="360-viewer-mode-toggle"
        :title="`Switch to ${isZoomMode ? 'Rotate' : 'Zoom'} mode`"
        @click="toggleViewMode"
      >
        <BaseIcon
          :name="currentModeIcon"
          :size="20"
          class="text-platform-text-1 transition-transform duration-200 group-hover:scale-110"
        />
        <span class="text-sm text-platform-text-1 font-medium">
          <!-- TODO: Add i18n -->
          {{ isZoomMode ? "Rotate" : "Zoom" }}
        </span>
      </button>
    </div>
  </BaseModal>
</template>

<style scoped lang="postcss">
:deep(.base-modal__content) {
  @apply h-full flex flex-col overflow-hidden relative;
}

:deep(.base-modal__container) {
  @apply h-screen max-h-screen overflow-hidden relative;
}

:deep(.base-360-viewer) {
  @apply w-full h-full;
}
</style>
